import React, { useEffect, useId, useRef, useState } from 'react';
import { ArrowUpRight, Minus, Plus } from 'lucide-react';
import Reveal from './Reveal';
import Lightbox from './Lightbox';
import { projects } from '../assets/work/galleryData';
import { GALLERY_BLUR } from '../assets/work/galleryBlur';
import { trackEvent } from '../analytics';
import { getScrollBehavior } from '../lib/motion';
import './CraftGallery.css';

// Presentation order is independent of the original gallery/lightbox order.
// These choices do not change a photo's provenance or claim job ownership.
const featuredIds = ['deck-primrose', 'cladding-timber-ceiling'];
const supportingIds = ['nans-deck', 'frame-caulfield'];
const curatedIds = [...featuredIds, ...supportingIds];
const featured = featuredIds.map((id) => projects.find((project) => project.id === id)).filter(Boolean);
const supporting = supportingIds.map((id) => projects.find((project) => project.id === id)).filter(Boolean);
const remaining = projects.filter((project) => !curatedIds.includes(project.id));

const imageSizes = {
  feature: '(max-width: 700px) calc(100vw - 40px), (max-width: 1100px) 55vw, 760px',
  secondary: '(max-width: 700px) calc(100vw - 40px), (max-width: 1100px) 40vw, 560px',
  supporting: '(max-width: 700px) calc(100vw - 40px), (max-width: 1100px) 40vw, 420px',
  additional: '(max-width: 700px) calc(100vw - 40px), (max-width: 1000px) 45vw, 420px',
};

const PhotoCard = ({ project, variant, delay = 0, onOpen }) => {
  // The formwork photos use existing 400/800 files; archive thumbnails are
  // 640/960. Keep descriptors honest even though the shared property names
  // predate those extra assets.
  const widths = project.id.startsWith('tommy-') ? [400, 800] : [640, 960];
  return (
    <Reveal as="button" type="button"
      className={`atelier-gallery-card atelier-gallery-card--${variant}`}
      delay={delay} onClick={() => onOpen(project)}
      aria-label={`View larger: ${project.caption}`}
      data-testid={`craft-gallery-${project.id}`}>
      <span className="atelier-gallery-photo"
        style={GALLERY_BLUR[project.id] ? { backgroundImage: `url("${GALLERY_BLUR[project.id]}")` } : undefined}>
        <picture>
          <source type="image/webp"
            srcSet={`${project.thumb.webp640} ${widths[0]}w, ${project.thumb.webp960} ${widths[1]}w`}
            sizes={imageSizes[variant]} />
          <img src={project.thumb.jpg} width="960" height="720"
            loading="lazy" decoding="async" alt={project.alt} />
        </picture>
      </span>
      <span className="atelier-gallery-caption">
        <span className="atelier-gallery-caption-copy">
          <span className="atelier-gallery-category">{project.category}</span>
          <span className="atelier-gallery-name">{project.caption}</span>
        </span>
        <span className="atelier-gallery-arrow" aria-hidden="true">
          <ArrowUpRight size={20} strokeWidth={1.5} />
        </span>
      </span>
    </Reveal>
  );
};

const CraftGallery = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const extraPhotosRef = useRef(null);
  const toggleRef = useRef(null);
  const focusAfterToggleRef = useRef(false);
  const extraPhotosId = useId();
  const titleId = useId();

  useEffect(() => {
    // The new cards precede the toggle in DOM order. Move keyboard users to
    // the first new card after it mounts, rather than skipping the collection.
    // This flag also prevents initial rendering from moving focus or scroll.
    if (!focusAfterToggleRef.current) return;
    focusAfterToggleRef.current = false;
    const target = expanded
      ? extraPhotosRef.current?.querySelector('button') || toggleRef.current
      : toggleRef.current;
    if (!target) return;
    target.focus({ preventScroll: true });
    target.scrollIntoView({ behavior: getScrollBehavior(), block: 'nearest' });
  }, [expanded]);

  const togglePhotos = () => {
    focusAfterToggleRef.current = true;
    setExpanded((value) => !value);
  };

  const openProject = (project) => {
    const index = projects.findIndex((item) => item.id === project.id);
    if (index < 0) return;
    trackEvent('gallery_open', { project_id: project.id, category: project.category });
    setActiveIndex(index);
  };

  return (
    <section className="atelier-gallery" id="my-work" aria-labelledby={titleId} data-testid="gallery-section">
      <div className="container">
        <Reveal as="header" className="atelier-gallery-heading">
          <div>
            <p className="atelier-gallery-eyebrow">Work &amp; inspiration</p>
            <h2 id={titleId}>A feel for the work.</h2>
          </div>
          <p className="atelier-gallery-intro">
            A mix of jobs I&rsquo;ve done and examples of the kind of work I can build for you.
          </p>
        </Reveal>

        <div className="atelier-gallery-featured">
          {featured.map((project, index) => (
            <PhotoCard key={project.id} project={project}
              variant={index === 0 ? 'feature' : 'secondary'} delay={index * 90} onOpen={openProject} />
          ))}
        </div>

        <div className="atelier-gallery-supporting">
          {supporting.map((project, index) => (
            <PhotoCard key={project.id} project={project} variant="supporting"
              delay={index * 70} onOpen={openProject} />
          ))}
        </div>

        <div ref={extraPhotosRef} id={extraPhotosId} className="atelier-gallery-additional" hidden={!expanded}>
          {expanded && remaining.map((project, index) => (
            <PhotoCard key={project.id} project={project} variant="additional"
              delay={Math.min(index % 3 * 60, 120)} onOpen={openProject} />
          ))}
        </div>

        <div className="atelier-gallery-bottom">
          <p>Select a photo for a closer look.</p>
          <button ref={toggleRef} type="button" className="atelier-gallery-toggle"
            aria-expanded={expanded} aria-controls={extraPhotosId}
            onClick={togglePhotos} data-testid="craft-gallery-toggle">
            <span>{expanded ? 'Show fewer photos' : `Show all ${projects.length} photos`}</span>
            {expanded ? <Minus size={18} strokeWidth={1.5} aria-hidden="true" />
              : <Plus size={18} strokeWidth={1.5} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {activeIndex !== null && (
        <Lightbox projects={projects} startIndex={activeIndex} onClose={() => setActiveIndex(null)} />
      )}
    </section>
  );
};

export default CraftGallery;
